import * as v from "@valibot/valibot";

// ============================================================
// API Schemas
// ============================================================

import { Address, Hex, UnsignedDecimal, UnsignedInteger } from "../../_schemas.ts";

/**
 * Initiate a withdrawal request.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#initiate-a-withdrawal-request
 */
export const Withdraw3Request = /* @__PURE__ */ (() => {
  return v.object({
    /** Action to perform. */
    action: v.object({
      /** Type of action. */
      type: v.literal("withdraw3"),
      /** Chain ID in hex format for EIP-712 signing. */
      signatureChainId: Hex,
      /** Hyperliquid network type. */
      hyperliquidChain: v.picklist(["Mainnet", "Testnet"]),
      /** Destination address. */
      destination: Address,
      /** Amount to withdraw (1 = $1). */
      amount: UnsignedDecimal,
      /** Nonce (timestamp in ms) used to prevent replay attacks. */
      time: UnsignedInteger,
    }),
    /** Nonce (timestamp in ms) used to prevent replay attacks. */
    nonce: UnsignedInteger,
    /** ECDSA signature components. */
    signature: v.object({
      /** First 32-byte component. */
      r: v.pipe(Hex, v.length(66)),
      /** Second 32-byte component. */
      s: v.pipe(Hex, v.length(66)),
      /** Recovery identifier. */
      v: v.picklist([27, 28]),
    }),
  });
})();
export type Withdraw3Request = v.InferOutput<typeof Withdraw3Request>;

/**
 * Successful response without specific data or error response.
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#initiate-a-withdrawal-request
 */
export type Withdraw3Response =
  | {
    /** Successful status. */
    status: "ok";
    /** Response details. */
    response: {
      /** Type of response. */
      type: "default";
    };
  }
  | {
    /** Error status. */
    status: "err";
    /** Error message. */
    response: string;
  };

// ============================================================
// Execution Logic
// ============================================================

import { parse } from "../../../_base.ts";
import { canonicalize } from "../../../signing/mod.ts";
import {
  type ExchangeConfig,
  type ExcludeErrorResponse,
  executeUserSignedAction,
  type ExtractRequestOptions,
} from "./_base/mod.ts";

/** Schema for action fields (excludes request-level system fields). */
const Withdraw3ActionSchema = /* @__PURE__ */ (() => {
  return v.omit(
    v.object(Withdraw3Request.entries.action.entries),
    ["signatureChainId", "hyperliquidChain", "time"],
  );
})();

/** Action parameters for the {@linkcode withdraw3} function. */
export type Withdraw3Parameters = Omit<v.InferInput<typeof Withdraw3ActionSchema>, "type">;

/** Request options for the {@linkcode withdraw3} function. */
export type Withdraw3Options = ExtractRequestOptions<v.InferInput<typeof Withdraw3Request>>;

/** Successful variant of {@linkcode Withdraw3Response} without errors. */
export type Withdraw3SuccessResponse = ExcludeErrorResponse<Withdraw3Response>;

/** EIP-712 types for the {@linkcode withdraw3} function. */
export const Withdraw3Types = {
  "HyperliquidTransaction:Withdraw": [
    { name: "hyperliquidChain", type: "string" },
    { name: "destination", type: "string" },
    { name: "amount", type: "string" },
    { name: "time", type: "uint64" },
  ],
};

/**
 * Initiate a withdrawal request.
 *
 * Signing: User-Signed EIP-712.
 *
 * @param config General configuration for Exchange API requests.
 * @param params Parameters specific to the API request.
 * @param opts Request execution options.
 * @return Successful response without specific data.
 *
 * @throws {ValidationError} When the request parameters fail validation (before sending).
 * @throws {TransportError} When the transport layer throws an error.
 * @throws {ApiRequestError} When the API returns an unsuccessful response.
 *
 * @example
 * ```ts
 * import { HttpTransport } from "@nktkas/hyperliquid";
 * import { withdraw3 } from "@nktkas/hyperliquid/api/exchange";
 * import { privateKeyToAccount } from "npm:viem/accounts";
 *
 * const wallet = privateKeyToAccount("0x..."); // viem or ethers
 * const transport = new HttpTransport(); // or `WebSocketTransport`
 *
 * await withdraw3({ transport, wallet }, {
 *   destination: "0x...",
 *   amount: "1",
 * });
 * ```
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/exchange-endpoint#initiate-a-withdrawal-request
 */
export function withdraw3(
  config: ExchangeConfig,
  params: Withdraw3Parameters,
  opts?: Withdraw3Options,
): Promise<Withdraw3SuccessResponse> {
  const action = canonicalize(
    Withdraw3ActionSchema,
    parse(Withdraw3ActionSchema, { type: "withdraw3", ...params }),
  );
  return executeUserSignedAction(config, action, Withdraw3Types, opts);
}
